import React, { useId, useMemo, useState, useEffect } from 'react';
import './index.css';

const NO_RESULTS_MESSAGE = 'No pairs found';
const FAVORITES_STORAGE_KEY = 'favoritePairs';

const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M15 15L19 19M1 9C1 11.1217 1.84285 13.1566 3.34315 14.6569C4.84344 16.1571 6.87827 17 9 17C11.1217 17 13.1566 16.1571 14.6569 14.6569C16.1571 13.1566 17 11.1217 17 9C17 6.87827 16.1571 4.84344 14.6569 3.34315C13.1566 1.84285 11.1217 1 9 1C6.87827 1 4.84344 1.84285 3.34315 3.34315C1.84285 4.84344 1 6.87827 1 9Z" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const PairSelector = ({
  pairsInfo = [],
  value,
  onChange,
  label = 'Pair',
  loading = false,
  error,
}) => {
  const searchInputId = useId();
  const selectId = useId();
  const [searchTerm, setSearchTerm] = useState('');
  const [favorites, setFavorites] = useState(() => {
    try {
      const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [isListOpen, setIsListOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
    } catch (error) {
      console.error('Failed to save favorites:', error);
    }
  }, [favorites]);

  const filteredPairs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    let matches = !term
      ? pairsInfo
      : pairsInfo.filter((pair) => pair.symbol.toLowerCase().includes(term));

    // Сортируем: избранные сверху
    matches = matches.sort((a, b) => {
      const aIsFavorite = favorites.includes(a.symbol);
      const bIsFavorite = favorites.includes(b.symbol);
      
      if (aIsFavorite && !bIsFavorite) return -1;
      if (!aIsFavorite && bIsFavorite) return 1;
      return 0;
    });

    if (!value) {
      return matches;
    }

    const activePair = pairsInfo.find((pair) => pair.symbol === value);
    if (!activePair) {
      return matches;
    }

    const hasActivePair = matches.some((pair) => pair.symbol === value);
    return hasActivePair ? matches : [activePair, ...matches];
  }, [pairsInfo, searchTerm, value, favorites]);

  const handleSelectChange = (event) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    if (!isListOpen && event.target.value) {
      setIsListOpen(true);
    }
  };

  const handlePairClick = (symbol) => {
    if (onChange) {
      onChange(symbol);
    }
    setIsListOpen(false);
    setSearchTerm('');
  };

  const handleSearchFocus = () => {
    setIsListOpen(true);
  };

  const handleSearchBlur = () => {
    // Задержка чтобы клик по элементу списка успел обработаться
    setTimeout(() => setIsListOpen(false), 200);
  };

  const hasResults = filteredPairs.length > 0;
  const selectValue = value ?? '';
  const displayedPairs = isListOpen ? filteredPairs.slice(0, 10) : [];

  return (
    <div className="pair-selector">
      <div className="pair-selector__container">
        <div className="pair-selector__search-container">
          <div className="pair-selector__search-wrapper">
            <SearchIcon />
            <input
              id={searchInputId}
              type="search"
              className="pair-selector__search"
              placeholder="Search pairs"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              autoComplete="off"
              aria-label="Search pairs"
            />
          </div>

          {isListOpen && hasResults && (
            <div className="pair-selector__list">
              {displayedPairs.map((pair) => {
                const isActive = pair.symbol === value;
                const isFavorite = favorites.includes(pair.symbol);
                return (
                  <div
                    key={pair.symbol}
                    className={`pair-selector__item${isActive ? ' pair-selector__item--active' : ''}`}
                    onClick={() => handlePairClick(pair.symbol)}
                  >
                    {isFavorite && <span className="pair-selector__item-star">★</span>}
                    <span className="pair-selector__item-text">{pair.symbol}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {loading && <p className="pair-selector__message">Loading pairs...</p>}
      {!loading && !hasResults && searchTerm && (
        <p className="pair-selector__message">{NO_RESULTS_MESSAGE}</p>
      )}
      {error && (
        <p className="pair-selector__message pair-selector__message--error">
          {error}
        </p>
      )}
    </div>
  );
};
